import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CompareCard from "@/components/feature/CompareCard";
import { creators, platformOptions, sortOptions } from "@/mocks/creators";
import { categories } from "@/mocks/categories";

const PAGE_SIZE = 6;

export default function Bang() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const [platform, setPlatform] = useState("全部平台");
  const [sort, setSort] = useState("heat");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const activeCat = searchParams.get("cat") ?? "all";
  const activeKeyword = searchParams.get("q") ?? "";

  const toggleCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") {
      next.delete("cat");
    } else {
      next.set("cat", slug);
    }
    setSearchParams(next);
    setVisible(PAGE_SIZE);
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = keyword.trim();
    if (value) {
      next.set("q", value);
    } else {
      next.delete("q");
    }
    setSearchParams(next);
    setVisible(PAGE_SIZE);
  };

  const filtered = useMemo(() => {
    const key = activeKeyword.trim().toLowerCase();
    const result = creators.filter((creator) => {
      const matchCat = activeCat === "all" || creator.categorySlug === activeCat;
      const matchPlatform = platform === "全部平台" || creator.platform === platform;
      const matchKey =
        !key ||
        creator.name.toLowerCase().includes(key) ||
        creator.category.toLowerCase().includes(key) ||
        creator.tags.some((tag) => tag.toLowerCase().includes(key)) ||
        creator.summary.toLowerCase().includes(key);
      return matchCat && matchPlatform && matchKey;
    });

    return result.sort((a, b) => {
      if (sort === "contrast") return b.contrastScore - a.contrastScore;
      if (sort === "recent") return b.afterYear - a.afterYear || b.id - a.id;
      return b.heat - a.heat;
    });
  }, [activeCat, activeKeyword, platform, sort]);

  const shown = filtered.slice(0, visible);
  const currentCategory = categories.find((item) => item.slug === activeCat);

  const listJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "网红反差对比榜单",
      description:
        "按领域整理的网红前后形象对比列表，支持关键词搜索与反差指数排序，快速定位想看的变化档案。",
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: "反差档案" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://fancha-archive.example/" },
        { "@type": "ListItem", position: 2, name: "对比榜单" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "网红反差对比收录列表",
      numberOfItems: filtered.length,
      itemListElement: filtered.slice(0, 10).map((creator, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: `${creator.name} 形象对比`,
        url: `https://fancha-archive.example/detail/${creator.slug}`,
      })),
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="网红反差榜单 - 网红反差前后对比对象大全 | 反差档案"
        description="按美妆、穿搭、知识、健身等领域整理的网红反差榜单，支持关键词搜索与反差指数排序，快速找到想看的前后形象对比对象。"
        keywords="网红反差榜单,网红反差对象,网红前后形象对比列表,网红反差排行"
        path="/bang"
        ogType="website"
        jsonLd={listJsonLd}
      />

      <main>
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "对比榜单" }]} />
            <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              {currentCategory ? `${currentCategory.name} · 反差对比榜单` : "网红反差对比榜单"}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {currentCategory
                ? currentCategory.desc
                : "共收录多领域创作者的公开形象变化档案。可以按领域筛选、按反差指数或热度排序，也可以直接搜名字。"}
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
            <div className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <form onSubmit={submitSearch} role="search" className="flex flex-col gap-3 sm:flex-row">
                <label className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3">
                  <i className="ri-search-line text-lg text-foreground-500"></i>
                  <span className="sr-only">搜索对比对象</span>
                  <input
                    type="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索博主名、领域或关键词"
                    aria-label="搜索对比对象"
                    className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
                  />
                </label>
                <button
                  type="submit"
                  className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  搜索
                </button>
                <select
                  aria-label="排序方式"
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </form>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">领域</span>
                <button
                  type="button"
                  onClick={() => toggleCategory("all")}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                    activeCat === "all"
                      ? "border-foreground-950 bg-foreground-950 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-400"
                  }`}
                >
                  全部
                </button>
                {categories.map((category) => (
                  <button
                    key={category.slug}
                    type="button"
                    onClick={() => toggleCategory(category.slug)}
                    className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                      activeCat === category.slug
                        ? "border-foreground-950 bg-foreground-950 text-background-50"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-400"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
                <span className="text-xs text-foreground-500">平台</span>
                {platformOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setPlatform(option)}
                    className={`cursor-pointer whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors ${
                      platform === option
                        ? "border-secondary-500 bg-secondary-100 text-secondary-900"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-400"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共找到 <strong className="text-foreground-950">{filtered.length}</strong> 组对比
                {activeKeyword && (
                  <span className="ml-2 text-foreground-500">关键词：{activeKeyword}</span>
                )}
              </p>
              {(activeCat !== "all" || activeKeyword || platform !== "全部平台") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchParams(new URLSearchParams());
                    setKeyword("");
                    setPlatform("全部平台");
                    setVisible(PAGE_SIZE);
                  }}
                  className="cursor-pointer whitespace-nowrap text-sm text-primary-600 hover:text-primary-700"
                >
                  清空筛选条件
                </button>
              )}
            </div>

            {shown.length > 0 ? (
              <>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {shown.map((creator) => (
                    <CompareCard key={creator.slug} creator={creator} summary />
                  ))}
                </div>

                {visible < filtered.length && (
                  <div className="mt-9 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                      className="cursor-pointer whitespace-nowrap rounded-md border border-foreground-950 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
                    >
                      加载更多（剩余 {filtered.length - visible} 组）
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <i className="ri-emotion-sad-line text-4xl text-background-500"></i>
                <p className="mt-4 font-heading text-lg font-bold text-foreground-900">
                  没有找到匹配的对比对象
                </p>
                <p className="mt-2 max-w-md text-sm text-foreground-600">
                  换个关键词试试，或者清空筛选条件浏览全部收录内容。也可以到联系页面提交你想看的对象。
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => {
                      setSearchParams(new URLSearchParams());
                      setKeyword("");
                      setPlatform("全部平台");
                    }}
                    className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    查看全部对比
                  </button>
                  <Link
                    to="/contact"
                    className="cursor-pointer whitespace-nowrap rounded-md border border-secondary-500 px-5 py-2.5 text-sm font-medium text-secondary-800 transition-colors hover:bg-secondary-100"
                  >
                    提交线索
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </SiteLayout>
  );
}